import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import { Form as FormioForm } from '@formio/react';
import { useReactToPrint } from 'react-to-print';
import OBForm from './OBForm';
import SocialCareReferralForm from './SocialCareReferralForm';
import { submitReferral } from './submitReferral';
import './ReferralFormio.css';

// Human friendly labels for the shared (section 2) referral info when printed.
const INFO_LABELS = {
  yourName: 'Your Name',
  yourContact: 'Your Contact Number',
  yourEmail: 'Your Email',
  yourAddress: 'Your Address',
  patientName: 'Patient Name',
  dob: 'Date of Birth',
  ssTin: 'SS/TIN',
  address: 'Patient Address',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  phone: 'Phone',
  dueDate: 'Estimated Due Date',
  preferredLanguage: 'Preferred Language',
  race: 'Race',
  interpreterRequired: 'Interpreter Required',
};

const PrintableInfoBlock = ({ info }) => {
  const entries = Object.entries(info || {}).filter(
    ([, val]) => val !== '' && val !== null && val !== undefined
  );
  if (!entries.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Referral Information
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {entries.map(([key, val]) => (
          <Grid size={{ xs: 6 }} key={key}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#475569' }}>
              {INFO_LABELS[key] || key}
            </Typography>
            <Typography variant="body2">{Array.isArray(val) ? val.join(', ') : String(val)}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const PrintableDataBlock = ({ title, data }) => {
  const entries = Object.entries(data || {}).filter(
    ([, val]) => val !== '' && val !== null && val !== undefined && val !== false && typeof val !== 'function'
  );
  if (!entries.length) return null;
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {entries.map(([key, val]) => (
          <Grid size={{ xs: 6 }} key={key}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#475569' }}>
              {key}
            </Typography>
            <Typography variant="body2">{Array.isArray(val) ? val.join(', ') : String(val)}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/**
 * ReferralFormModal
 * Hosts the recipient-specific (or generic) referral form on top of the portal screen.
 * Section 2 (shared referral info) is collected on the portal and merged here at submit / print.
 */
const ReferralFormModal = ({
  open,
  onClose,
  recipient,
  referralInfo,
  patientAddressObj,
  printIncludeReferrerInfo = true,
  onSubmitted,
}) => {
  const {
    mode = 'standard',
    companyId = null,
    companyName = '',
    activeSetting = null,
    dynamicFormSchema = null,
  } = recipient || {};

  const isInternal = mode === 'company' && !!companyId;

  // Controlled state for the standard / social-care forms.
  const [formData, setFormData] = useState({});
  // Live data captured from the dynamic Form.io form.
  const [formioData, setFormioData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // The recipient-specific data that the user filled in the modal.
  const recipientData = dynamicFormSchema ? formioData : formData;

  // Combined payload data: shared section 2 + recipient specific form.
  const combinedData = useMemo(
    () => ({ ...(referralInfo || {}), ...(recipientData || {}) }),
    [referralInfo, recipientData]
  );

  const clientName = (referralInfo?.clientName || recipientData?.clientName || '').trim();
  const initiatingCompanyName = (referralInfo?.initiatingCompanyName || '').trim();

  const doSubmit = async (overrideData) => {
    if (!isInternal || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const data = overrideData ? { ...(referralInfo || {}), ...overrideData } : combinedData;
    const result = await submitReferral({
      companyId,
      companyName,
      activeSetting,
      initiatingCompanyName,
      clientName,
      clientAddressObj: patientAddressObj || null,
      data,
    });
    setSubmitting(false);
    if (result.success) {
      onSubmitted?.(result);
    } else {
      setSubmitError(result.error || 'Something went wrong. Please try again.');
    }
  };

  const formTitle = dynamicFormSchema?.title
    || (isInternal ? `${companyName} Referral Form` : 'Standard FieldWorker Referral Form');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        {formTitle}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ m: 2, mb: 0 }}>
            Referral was not saved: {submitError}
          </Alert>
        )}
        {/* printRef wraps the live rendered content so react-to-print captures filled values */}
        <Box ref={printRef} sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Referral context header — shown inside modal and captured when printing */}
          {mode !== 'standard' && (companyName || initiatingCompanyName || referralInfo?.clientName) && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: '#f0f7ff',
                borderRadius: 1,
                border: '1px solid #bfdbfe',
                ...(!printIncludeReferrerInfo && { '@media print': { display: 'none' } }),
              }}
            >
              {companyName && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e40af' }}>
                  Receiving: {companyName}
                </Typography>
              )}
              {initiatingCompanyName && (
                <Typography variant="body2" color="text.secondary">
                  Referring: {initiatingCompanyName}
                </Typography>
              )}
              {referralInfo?.clientName && (
                <Typography variant="body2" color="text.secondary">
                  Client: {referralInfo.clientName}
                </Typography>
              )}
              {referralInfo?.address && (
                <Typography variant="body2" color="text.secondary">
                  Address: {referralInfo.address}
                </Typography>
              )}
            </Box>
          )}

          {dynamicFormSchema?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {dynamicFormSchema.description}
            </Typography>
          )}

          {dynamicFormSchema ? (
            <Box className="fw-referral-formio-wrapper">
              <FormioForm
                className="fw-referral-formio"
                form={dynamicFormSchema}
                onChange={(submission) => setFormioData(submission?.data || {})}
                onSubmit={(submission) => {
                  const data = submission?.data || {};
                  setFormioData(data);
                  if (isInternal) doSubmit(data);
                }}
                options={{
                  noAlerts: false,
                  buttonSettings: { showCancel: false },
                  breadcrumbSettings: { clickable: true },
                  editForm: { feedback: false },
                }}
              />
            </Box>
          ) : isInternal ? (
            <SocialCareReferralForm
              form={formData}
              onChange={handleFieldChange}
              onSubmit={(e) => { e.preventDefault(); doSubmit(); }}
              hideActions
            />
          ) : (
            <OBForm
              form={formData}
              onChange={handleFieldChange}
              onSubmit={(e) => e.preventDefault()}
              hideActions
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {!isInternal && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Non-FieldWorker recipient — use “Print Form” to generate a physical document.
          </Typography>
        )}
        {isInternal && !dynamicFormSchema && (
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={submitting}
            onClick={() => doSubmit()}
          >
            Submit Securely
          </Button>
        )}
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
          Print Form
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default ReferralFormModal;
